import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { format } from 'date-fns'
import { Button } from '@/atoms/Button'
import { FireIcon } from '@/atoms/Icons/FireIcon'
import { MusicIcon } from '@/atoms/Icons/MusicIcon'
import { EyebrowSM, TitleXS } from '@/atoms/Text'
import { TheatricalShowtime } from '@/types/codegen-federation'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { SeatMapLayout } from '@/views/TicketCheckoutViews/SeatMapView/components/SeatMap'
import { AvailableSeatType } from '@/views/TicketCheckoutViews/SeatMapView/components/useAvailableSeatTypes'
import { isShowtimeSoldOut } from '../../utils/utils'
import { useTicketsContext } from '../TicketsContext'
import { transformSeats } from './utils'

interface SeatAvailabilityShowtimeItemProps {
  showtime: TheatricalShowtime
  closeAvailabilityDrawer: () => void
  onClick: (showtime: TheatricalShowtime) => void
  isSellingFast: boolean
}

export const SeatAvailabilityShowtimeItem: FC<SeatAvailabilityShowtimeItemProps> = ({
  showtime,
  closeAvailabilityDrawer,
  onClick,
  isSellingFast,
}) => {
  const { t } = useTranslate('tickets')
  const { disableShowtimes } = useTicketsContext()

  const transformedSeats = transformSeats(showtime)

  // Calculate available seats and total capacity
  const availableSeats = useMemo(() => {
    if (showtime.availableInventory) return showtime.availableInventory
    if (showtime.seatmap?.seats) return showtime.seatmap.seats.filter((seat) => seat.available).length
    return 0
  }, [showtime.seatmap, showtime.availableInventory])

  const totalSeats = useMemo(() => {
    if (showtime.totalSeats) return showtime.totalSeats
    if (showtime.seatmap?.seats) return showtime.seatmap.seats.length
    return 0
  }, [showtime.seatmap, showtime.totalSeats])

  // Format date as "December 18"
  const formattedDateLong = useMemo(() => {
    if (!showtime.utcStartTime || !showtime.localTimeOffset) return ''
    try {
      const utcDate = new Date(showtime.utcStartTime)
      const tzDifference = parseFloat(showtime.localTimeOffset) * 60 + utcDate.getTimezoneOffset()
      const localDate = new Date(utcDate.getTime() + tzDifference * 60 * 1000)
      return format(localDate, 'MMMM d')
    } catch (error) {
      return ''
    }
  }, [showtime.utcStartTime, showtime.localTimeOffset])

  const hasSeatmap = !!showtime.seatmap
  const availableSeatTypes: AvailableSeatType[] = [] // Empty array as seatmap doesn't include seatTypesPresent
  const isSoldOut = isShowtimeSoldOut(showtime)

  const handleClick = () => {
    if (showtime.releaseType === 'SING_ALONG') {
      closeAvailabilityDrawer()
    }
    onClick(showtime)
  }

  return (
    <div className="border-b border-core-gray-300 py-6 last:border-b-0">
      <div className="flex max-w-[480px] flex-row gap-5">
        {/* Seatmap Preview */}
        <div
          className={classNames(
            'relative h-[120px] w-[160px] shrink-0 overflow-hidden rounded-lg bg-core-gray-950 lg:w-[208px]',
            hasSeatmap && 'cursor-pointer',
          )}
          onClick={hasSeatmap ? handleClick : undefined}
        >
          {showtime.seatmap ? (
            <div className="pointer-events-none h-full w-full p-2">
              <SeatMapLayout
                rows={showtime.seatmap.rows}
                columns={showtime.seatmap.cols}
                seats={transformedSeats}
                onSeatPressed={() => {}}
                selectedSeats={new Map()}
                onOpenSeatLegend={() => {}}
                availableSeatTypes={availableSeatTypes}
                isViewOnly={true}
              />
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-core-gray-950">
              <span className="text-sm text-white">{t('previewUnavailable', 'Preview Unavailable')}</span>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="flex flex-1 flex-col gap-1 border-core-gray-300">
          <EyebrowSM color="core-gray-700">{formattedDateLong}</EyebrowSM>
          <TitleXS weight="semibold">{showtime.localStartTimeFriendly}</TitleXS>
          <div className="flex items-center gap-1">
            <TitleXS>
              <Translate t={t} i18nKey="xOfTotalSeatsLeft" values={{ availableSeats, totalSeats }}>
                <span className="font-semibold">
                  {{ availableSeats }} of {{ totalSeats }}
                </span>{' '}
                seats left
              </Translate>
            </TitleXS>
            {isSellingFast && <FireIcon color="error-400" size={20} />}
          </div>

          <Button
            disabled={isSoldOut || disableShowtimes}
            variant="oxide-300"
            onClick={handleClick}
            className="mt-auto w-full gap-1"
          >
            {showtime.releaseType === 'SING_ALONG' && <MusicIcon size={16} />}
            {isSoldOut ? t('soldOut', 'Sold Out') : t('pickSeats', 'Pick Seats')}
          </Button>
        </div>
      </div>
    </div>
  )
}
